/**
 * State for the generic translation editor: one (field x locale) grid, built entirely from
 * `GET /admin/translation/schema/{type}`.
 *
 * Nothing in here names a locale, a field or a type. `locales` and `fields` come from the schema
 * response, so enabling a 10th language on the server adds a column to every form that hosts the
 * panel with no code change on this side — that is the acceptance criterion for the whole feature,
 * and the reason the schema call exists at all.
 *
 * ## The save seam — translations save THEMSELVES, not with the parent form
 *
 * Deliberate, and the three reasons are all correctness rather than taste:
 *
 *  1. **A translation needs an entity id and a new entity has none.** The parent form's save is
 *     what creates the row, so there is no id to write translations against until after it
 *     returns. Bundling would mean a two-phase save inside every host form.
 *  2. **Saving an exercise can change its id.** `AdminExerciseService` forks a company variant when
 *     a company edits a system exercise, and `ExercisePage` observes `savedExercise.id !==
 *     exercise.id` and navigates. Translations collected before that save belong to the NEW row;
 *     a bundled payload would have written them to the old one, silently, where nobody would see
 *     them again.
 *  3. **Different endpoints, different authorization.** Entity writes and `@authz.canTranslate` are
 *     separate checks, and TEMPLATE/TEMPLATE_WORKOUT refuse translation while accepting entity
 *     edits. A single save button would make a 403 on the translation half look like a failed
 *     entity save, or worse, roll one back after the other committed.
 *
 * So: the panel has its own save action per locale, the parent form's dirty tracking and payload
 * are untouched, and the host passes `reloadToken` (bumped after its own save) so the panel
 * re-reads the canonical text and the freshly-recomputed staleness flags.
 *
 * ## Blank means CLEAR
 *
 * The server soft-deletes a field whose incoming value is blank, so it falls back through the
 * locale chain rather than rendering an empty label. "No change" therefore has to be expressed by
 * omitting the key. `pendingValues` builds exactly that diff: changed fields only, blanks included.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    getTranslationSchema,
    getEntityTranslations,
    saveTranslations,
    clearTranslations,
} from '../services/TranslationService';
import type {
    EntityTranslations,
    TranslatableTypeCode,
    TranslationSchema,
    TranslationValues,
} from '../services/TranslationService';

/** locale -> field key -> value currently in the inputs. Canonical locale is never a key here. */
export type LocaleDrafts = Record<string, TranslationValues>;

export type TranslationPanelStatus =
    /** No entity id yet — the host has not saved the row, so there is nothing to translate. */
    | 'no-entity'
    | 'loading'
    | 'ready'
    /** The server refused this (type, id) pair. A state to render, not an error to report. */
    | 'forbidden'
    /**
     * This PERSONA has no translation endpoint at all yet (COMPANY_GROUP_ADMIN). Kept distinct from
     * `forbidden` on purpose: "this item cannot be translated here" and "your library's translation
     * API does not exist yet" send someone looking in completely different places.
     */
    | 'unsupported-persona'
    | 'error';

export interface UseEntityTranslationsOptions {
    type: TranslatableTypeCode;
    entityId: number | null | undefined;
    /** Change this to force a re-read — e.g. after the host form saved new canonical text. */
    reloadToken?: number | string;
}

/** What one stored translation looked like when it was loaded, for diffing and staleness. */
const loadedValue = (data: EntityTranslations | null, locale: string, field: string): string =>
    data?.translations?.[locale]?.[field]?.value || '';

const isStale = (data: EntityTranslations | null, locale: string, field: string): boolean =>
    Boolean(data?.translations?.[locale]?.[field]?.stale);

export const useEntityTranslations = ({
    type,
    entityId,
    reloadToken,
}: UseEntityTranslationsOptions) => {
    const [status, setStatus] = useState<TranslationPanelStatus>(
        entityId ? 'loading' : 'no-entity',
    );
    const [schema, setSchema] = useState<TranslationSchema | null>(null);
    const [loaded, setLoaded] = useState<EntityTranslations | null>(null);
    const [drafts, setDrafts] = useState<LocaleDrafts>({});
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    /** The locale currently being written, so only its own row shows a spinner. */
    const [savingLocale, setSavingLocale] = useState<string | null>(null);
    /** Set when a WRITE is refused though the read succeeded — render read-only, don't blow up. */
    const [writeForbidden, setWriteForbidden] = useState(false);
    const [savedLocale, setSavedLocale] = useState<string | null>(null);

    /**
     * Guards against a late response from a previous (entityId, reloadToken) overwriting a newer
     * one. The panel is remounted on navigation but NOT on an id change within the same page (the
     * variant-fork case), so this is reachable.
     */
    const requestRef = useRef(0);

    const load = useCallback(async () => {
        if (!entityId) {
            setStatus('no-entity');
            setLoaded(null);
            setDrafts({});
            return;
        }
        const request = requestRef.current + 1;
        requestRef.current = request;

        setStatus('loading');
        setErrorMessage(null);
        setWriteForbidden(false);
        // NOTE: `savedLocale` is deliberately NOT reset here. A successful write ends with a
        // re-read through this same function, and clearing it would wipe the "saved" confirmation
        // the write just earned. It is cleared when the user edits again, or when the panel is
        // pointed at a different row (see the effect below).

        const [schemaResult, entityResult] = await Promise.all([
            getTranslationSchema(type),
            getEntityTranslations(type, entityId),
        ]);
        if (requestRef.current !== request) {
            return; // a newer load started; its result is the one that counts
        }

        // The persona has no endpoint at all (COMPANY_GROUP_ADMIN — no /groupAdmin/translation
        // controller yet). Reported separately from a 403 so the message names the real cause.
        if (
            (!schemaResult.ok && schemaResult.reason === 'unsupported-persona') ||
            (!entityResult.ok && entityResult.reason === 'unsupported-persona')
        ) {
            setStatus('unsupported-persona');
            setErrorMessage(
                (!schemaResult.ok && schemaResult.message) ||
                    (!entityResult.ok && entityResult.message) ||
                    null,
            );
            return;
        }

        // A 403 on either call is the fail-closed verdict for this (type, id) pair: TEMPLATE and
        // TEMPLATE_WORKOUT refuse by design, and a cross-company id refuses for real. Both render
        // the same "not translatable here" state rather than an error or a spinner that never ends.
        if (
            (!schemaResult.ok && schemaResult.status === 403) ||
            (!entityResult.ok && entityResult.status === 403)
        ) {
            setStatus('forbidden');
            return;
        }
        if (!schemaResult.ok) {
            setStatus('error');
            setErrorMessage(schemaResult.message);
            return;
        }
        if (!entityResult.ok) {
            setStatus('error');
            setErrorMessage(entityResult.message);
            return;
        }

        const nextSchema = schemaResult.data;
        const nextLoaded = entityResult.data;
        const canonical = nextSchema.canonicalLocale;

        // One draft entry per (non-canonical locale, field) from the SCHEMA — not from the stored
        // rows. An untranslated locale must still get an empty input, which is the only way a
        // translator can see that it is missing.
        const nextDrafts: LocaleDrafts = {};
        (nextSchema.locales || []).forEach((locale) => {
            if (!locale.code || locale.code === canonical) return;
            const perField: TranslationValues = {};
            (nextSchema.fields || []).forEach((field) => {
                if (!field.key) return;
                perField[field.key] = loadedValue(nextLoaded, locale.code as string, field.key);
            });
            nextDrafts[locale.code] = perField;
        });

        setSchema(nextSchema);
        setLoaded(nextLoaded);
        setDrafts(nextDrafts);
        setStatus('ready');
        // `reloadToken` is not READ in here — it is the host's explicit "re-read now" signal, so it
        // has to re-create this callback and refire the effect below. That is the whole mechanism.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type, entityId, reloadToken]);

    useEffect(() => {
        load();
    }, [load]);

    /** A different row (or a host-forced reload) is a clean slate for the save confirmation. */
    useEffect(() => {
        setSavedLocale(null);
    }, [type, entityId, reloadToken]);

    const setDraft = useCallback((locale: string, field: string, value: string) => {
        setSavedLocale(null);
        setDrafts((prev) => ({
            ...prev,
            [locale]: { ...(prev[locale] || {}), [field]: value },
        }));
    }, []);

    /**
     * The PUT body for one locale: ONLY the fields whose value differs from what was loaded.
     *
     * A field the user blanked is included with "" — that is the clear signal. A field the user
     * never touched is omitted, which is what keeps a save from wiping the other fields.
     * Whitespace-only is normalized to "" so "   " clears rather than storing blanks the server
     * would treat as present-but-empty.
     */
    const pendingValues = useCallback(
        (locale: string): TranslationValues => {
            const draft = drafts[locale] || {};
            const body: TranslationValues = {};
            Object.keys(draft).forEach((field) => {
                const next = (draft[field] || '').trim();
                const before = loadedValue(loaded, locale, field).trim();
                if (next !== before) {
                    body[field] = next;
                }
            });
            return body;
        },
        [drafts, loaded],
    );

    /** Locales with at least one changed field, in schema order. */
    const dirtyLocales = useMemo(() => {
        if (status !== 'ready') return [];
        return (schema?.locales || [])
            .map((locale) => locale.code)
            .filter((code): code is string => Boolean(code) && code !== schema?.canonicalLocale)
            .filter((code) => Object.keys(pendingValues(code)).length > 0);
    }, [status, schema, pendingValues]);

    const isDirty = dirtyLocales.length > 0;

    const applyWriteFailure = useCallback((status?: number, message?: string) => {
        if (status === 403) {
            setWriteForbidden(true);
            setErrorMessage('אין לך הרשאה לערוך תרגומים לפריט זה');
            return;
        }
        setErrorMessage(message || 'שגיאה בשמירת התרגום');
    }, []);

    const saveLocale = useCallback(
        async (locale: string): Promise<boolean> => {
            if (!entityId) return false;
            const body = pendingValues(locale);
            if (Object.keys(body).length === 0) return true;

            setSavingLocale(locale);
            setErrorMessage(null);
            const result = await saveTranslations(type, entityId, locale, body);
            setSavingLocale(null);

            if (!result.ok) {
                applyWriteFailure(result.status, result.message);
                return false;
            }
            // Re-read rather than patching local state: a write recomputes staleness for EVERY
            // locale (a canonical edit invalidates the rest), and only the server knows the result.
            await load();
            setSavedLocale(locale);
            return true;
        },
        [entityId, type, pendingValues, applyWriteFailure, load],
    );

    const saveAll = useCallback(async (): Promise<boolean> => {
        if (!entityId) return false;
        const locales = dirtyLocales;
        if (locales.length === 0) return true;

        setSavingLocale(locales[0]);
        setErrorMessage(null);
        for (const locale of locales) {
            setSavingLocale(locale);
            const result = await saveTranslations(type, entityId, locale, pendingValues(locale));
            if (!result.ok) {
                setSavingLocale(null);
                applyWriteFailure(result.status, result.message);
                return false;
            }
        }
        setSavingLocale(null);
        await load();
        setSavedLocale(locales[locales.length - 1]);
        return true;
    }, [entityId, type, dirtyLocales, pendingValues, applyWriteFailure, load]);

    const clearLocale = useCallback(
        async (locale: string): Promise<boolean> => {
            if (!entityId) return false;
            setSavingLocale(locale);
            setErrorMessage(null);
            const result = await clearTranslations(type, entityId, locale);
            setSavingLocale(null);
            if (!result.ok) {
                applyWriteFailure(result.status, result.message);
                return false;
            }
            await load();
            return true;
        },
        [entityId, type, applyWriteFailure, load],
    );

    /** True when the locale has anything stored on the server (so clearing it would do something). */
    const hasStored = useCallback(
        (locale: string) => {
            const stored = loaded?.translations?.[locale];
            if (!stored) return false;
            return Object.keys(stored).some((field) => Boolean(stored[field]?.value));
        },
        [loaded],
    );

    return {
        status,
        errorMessage,
        /** Schema-derived: the fields to render, in server order. */
        fields: schema?.fields || [],
        /** Schema-derived: every enabled locale, canonical included (the panel shows it read-only). */
        locales: schema?.locales || [],
        canonicalLocale: schema?.canonicalLocale,
        /** field key -> the canonical text being translated FROM. */
        canonical: loaded?.canonical || {},
        drafts,
        setDraft,
        staleFor: (locale: string, field: string) => isStale(loaded, locale, field),
        /** Stale only matters while the input still shows the stored value. */
        showStale: (locale: string, field: string) =>
            isStale(loaded, locale, field) &&
            (drafts[locale]?.[field] || '') === loadedValue(loaded, locale, field),
        dirtyLocales,
        isDirty,
        isLocaleDirty: (locale: string) => dirtyLocales.includes(locale),
        hasStored,
        savingLocale,
        savedLocale,
        writeForbidden,
        saveLocale,
        saveAll,
        clearLocale,
        reload: load,
    };
};

export default useEntityTranslations;
